import { Link } from 'react-router-dom';
import {
  NavigationCustomizationItemDto,
  NavigationDto,
  PageDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  getTopNavigationLinkContent,
  type NavigationItemData,
  TopNavigation as PortalUiTopNavigation,
} from '@asset-metrix/portal-ui';

import { NO_ROLE_ID } from '../../api/portalGatewayClient';
import { useStore } from '../../store';
import { pageWithRoleLink } from '../../utils/urlPath';

import { getActiveItem, getActiveItemIndex } from './activeItems';

type TopNavigationRouterLinkProps = {
  activeSlug: string;
  item: NavigationItemData;
  navigation: NavigationItemData[];
  onClick: () => void;
  pageType: PageDto['type'];
  urlSearchString: string;
};

function TopNavigationRouterLink({
  activeSlug,
  item,
  navigation,
  onClick,
  pageType,
  urlSearchString,
}: TopNavigationRouterLinkProps) {
  const roleId = useStore((state) => state.activeUserRole?.id);

  return (
    <Link
      {...getTopNavigationLinkContent({ activeSlug, item, navigation })}
      to={pageWithRoleLink(roleId ?? NO_ROLE_ID, {
        pageType,
        slug: `${item.completeSlug}${urlSearchString.length > 0 ? '?' + urlSearchString : ''}`,
      })}
      onClick={onClick}
    />
  );
}

type TopNavigationProps = {
  navigationData?: NavigationDto;
  pageType: PageDto['type'];
  slug?: string;
  activeDynamicNavigationItem?: NavigationCustomizationItemDto;
};

export const TopNavigation = ({
  navigationData,
  pageType,
  slug,
  activeDynamicNavigationItem,
}: TopNavigationProps) => {
  if (!navigationData || slug == null) {
    return null;
  }

  const activeMainItem = getActiveItem(
    navigationData[
      pageType === 'page'
        ? 'rootNavigationItems'
        : 'detailPageRootNavigationItems'
    ],
    slug
  );
  if (activeMainItem?.type !== 'navigation-menu-item') {
    return null;
  }

  const activeColumnIndex = getActiveItemIndex(activeMainItem.items, slug);

  const urlParts = activeDynamicNavigationItem?.href.split('?');
  const urlSearchString =
    urlParts != null && urlParts?.length > 1 ? (urlParts.pop() ?? '') : '';

  return (
    <PortalUiTopNavigation
      activeColumnIndex={activeColumnIndex}
      activeSlug={slug}
      navigation={activeMainItem.items}
      Link={(item, close) => (
        <TopNavigationRouterLink
          activeSlug={slug}
          item={item}
          navigation={activeMainItem.items}
          onClick={close}
          pageType={pageType}
          urlSearchString={urlSearchString}
        />
      )}
    />
  );
};
